import API_URL from '../config/API_URL';

const API_BASE = `${API_URL}/api/voice`;

export const voiceService = {
  /**
   * Create a voice clone from existing recordings
   */
  async createVoiceClone(readerId, recordingIds, voiceName) {
    try {
      const formData = new FormData();
      
      // Add recording IDs
      recordingIds.forEach(id => {
        formData.append('recording_ids', id);
      });
      
      // Add voice name
      formData.append('voice_name', voiceName);
      formData.append('readerId', readerId);

      const response = await fetch(`${API_URL}/api/voice/clone/create`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create voice clone');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating voice clone:', error);
      throw error;
    }
  },

  /**
   * Get current user's voice clone
   */
  async getMyVoiceClone() {
    const response = await fetch(`${API_BASE}/clone/my-voice`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch voice clone');
    }

    return response.json();
  },

  /**
   * Check if AI generation is available for a book/reader/kid combo
   */
  async checkAvailability(bookId, readerId, kidId) {
    const response = await fetch(`${API_BASE}/check-availability`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        book_id: bookId,
        reader_id: readerId,
        kid_id: kidId,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to check availability');
    }

    return response.json();
  },

  /**
   * Generate AI story audio (creates ai_recording, not recording)
   */
  async generateStory(bookId, readerId, kidId, bookText = null) {
    const response = await fetch(`${API_BASE}/generate-story`, {
      method: 'POST',
      credentials: 'include',

      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        book_id: bookId,
        reader_id: readerId,
        kid_id: kidId,
        book_text: bookText, // Optional - backend will extract from PDF if null
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to generate story');
    }

    return response.json();
  },

  /**
   * Get a specific AI recording
   */
  async getAIRecording(aiRecordingId) {
    const response = await fetch(`${API_BASE}/ai-recordings/${aiRecordingId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch AI recording');
    }

    return response.json();
  },

  /**
   * Get presigned URL for AI audio playback
   */
  async getAIRecordingPresignedUrl(aiRecordingId) {
    const response = await fetch(`${API_BASE}/ai-recordings/${aiRecordingId}/presigned-url`);
    
    if (!response.ok) {
      throw new Error('Failed to get presigned URL');
    }

    return response.json();
  },

  /**
   * Get all AI recordings for a kid
   */
  async getKidAIRecordings(kidId) {
    const response = await fetch(`${API_BASE}/kid/${kidId}/ai-recordings`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch AI recordings');
    }

    return response.json();
  },

  /**
   * Get BOTH manual and AI recordings for a kid
   */
  async getKidAllRecordings(kidId) {
    const response = await fetch(`${API_BASE}/kid/${kidId}/all-recordings`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch recordings');
    }

    return response.json();
  },

  /**
   * Get subscription status for all families
   */
  async getSubscriptionStatus() {
    const response = await fetch(`${API_BASE}/subscription/status`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch subscription status');
    }

    return response.json();
  },

  /**
   * Get specific family's subscription
   */
  async getFamilySubscription(familyId) {
    const response = await fetch(`${API_BASE}/subscription/family/${familyId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch family subscription');
    }

    return response.json();
  },

  /**
   * Create trial subscription (for testing)
   */
  async createTrialSubscription(familyId) {
    const response = await fetch(`${API_BASE}/subscription/create-trial`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        family_id: familyId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create trial');
    }

    return response.json();
  },
};